import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import { ProductThumbnail } from '../../../Helpers/ThumbHelper';
import { SLOT_LABELS } from '../../../Helpers/PatternHelper';
import styles from './CustomizeColors.module.css';
import loopImg from './assets/loop.svg';
import cutImg from './assets/cut.svg';
import cutLoopImg from './assets/loopcut.svg';
import tipShearImg from './assets/tipShear.png';

const TEXTURE_TYPES = {
    'L': 'Loop',
    'C': 'Cut',
    'CL': 'Cut Loop',
    'TS': 'Tip Shear',
    'TSL': 'Tip Shear/Loop',
    'B': 'Background',
};

const TEXTURE_IMAGES = {
    'L': loopImg,
    'C': cutImg,
    'CL': cutLoopImg,
    'TS': tipShearImg,
    'TSL': [tipShearImg, loopImg],
    'B': null,
};

export default function CustomizeColors() {
    const dispatch = useDispatch();
    const chosenPattern = useSelector(state => state.app.chosenPattern);
    const colorData = useSelector(state => state.app.seriesData[8623]) || {};
    const patternColors = useSelector(state => state.app.patternColors);
    const selectedColorIndex = useSelector(state => state.app.selectedColorIndex);
    const [displayColors, setDisplayColors] = useState([]);

    const lastPatternId = useRef(null);

    // Get unique texture types from displayColors (deduped/sorted colors actually shown)
    const getUniqueTextureTypes = () => {
        if (displayColors.length === 0) return [];
        const unique = [...new Set(displayColors.map(c => c.textureCode).filter(Boolean))];
        return unique.map(type => ({
            code: type,
            label: TEXTURE_TYPES[type] || type
        }));
    };

    const uniqueTextureTypes = getUniqueTextureTypes();

    // Initialize patternColors using unsorted warranty
    useEffect(() => {
        if (!chosenPattern || !chosenPattern.warranty) {
            lastPatternId.current = null;
            return;
        }

        const patternId = chosenPattern.id;

        if (patternId === lastPatternId.current) {
            return;
        }

        if (patternColors.length > 0) {
            lastPatternId.current = patternId;
            return;
        }

        const colorDataKeys = Object.keys(colorData);
        if (colorDataKeys.length === 0) {
            return;
        }

        lastPatternId.current = patternId;

        // Initialize colors from pattern warranty (unsorted)
        const colorIds = chosenPattern.warranty.split(',').map(id => id.trim());
        // Get texture types for labels
        const textureTypesForLabels = chosenPattern.defaultRoomSceneSpecifier
            ? chosenPattern.defaultRoomSceneSpecifier.split(',').map(t => t.trim())
            : [];

        // Build a map of each ID to all its texture codes
        const idTextureMap = {};
        colorIds.forEach((id, idx) => {
            const cleanedId = String(id).replace(/\D/g, '');
            const code = textureTypesForLabels[idx] || '';
            if (!idTextureMap[cleanedId]) idTextureMap[cleanedId] = new Set();
            idTextureMap[cleanedId].add(code);
        });

        const resolveTextureCode = (cleanedId) => {
            const codes = idTextureMap[cleanedId] || new Set();
            if (codes.has('CL') && codes.has('L')) return 'TSL';
            if (codes.has('CL') && codes.has('C')) return 'TS';
            if (codes.has('CL')) return 'TS';
            if (codes.has('C') && codes.has('L')) return 'TS';
            if (codes.has('L')) return 'L';
            if (codes.has('C')) return 'C';
            return '';
        };

        const colors = colorIds.map((id, index) => {
            const cleanedId = String(id).replace(/\D/g, '');
            const colorProduct = colorData[cleanedId];
            const textureCode = resolveTextureCode(cleanedId);
            const label = textureCode ? (TEXTURE_TYPES[textureCode] || textureCode) : `${index + 1}`;

            return {
                index,
                originalId: id,
                currentId: cleanedId,
                label,
                textureCode,
                sku: colorProduct?.sku || `#${cleanedId}`,
                product: colorProduct || null
            };
        });

        dispatch(appActions.setPatternColors(colors));
        dispatch(appActions.setSelectedColorIndex(null));
    }, [chosenPattern, colorData, patternColors.length, dispatch]);

    // Initialize displayColors (sorted, deduped) when pattern changes
    useEffect(() => {
        if (!chosenPattern || !chosenPattern.warranty || patternColors.length === 0) {
            return;
        }

        if (Object.keys(colorData).length === 0) {
            return;
        }

        const warrantyIds = chosenPattern.warranty
            .split(',')
            .map(id => id.trim())
            .filter(Boolean);

        const styleOrder = (chosenPattern.thumbnailRenderOptions || '')
            .split(',')
            .map(num => parseInt(num.trim(), 10));

        const textureTypesForLabels = chosenPattern.defaultRoomSceneSpecifier
            ? chosenPattern.defaultRoomSceneSpecifier.split(',').map(t => t.trim())
            : [];

        // Step 1: Build a map of each ID to all its texture codes
        const idTextureMap = {};
        warrantyIds.forEach((id, idx) => {
            const code = textureTypesForLabels[idx] || '';
            if (!idTextureMap[id]) idTextureMap[id] = new Set();
            idTextureMap[id].add(code);
        });

        // Resolve texture code for an ID based on its combined occurrences
        const resolveTextureCode = (id) => {
            const codes = idTextureMap[id] || new Set();
            if (codes.has('CL') && codes.has('L')) return 'TSL'; // Tip Shear/Loop
            if (codes.has('CL') && codes.has('C')) return 'TS';  // Tip Shear
            if (codes.has('CL')) return 'TS';                     // CL alone = Tip Shear
            if (codes.has('C') && codes.has('L')) return 'TS';   // C + L = Tip Shear
            if (codes.has('L')) return 'L';
            if (codes.has('C')) return 'C';
            return '';
        };

        // Step 2: sort using style
        const orderedWarranty = warrantyIds
            .map((id, originalIndex) => ({
                id,
                originalIndex,
                sort: styleOrder[originalIndex] ?? originalIndex + 1,
                textureCode: resolveTextureCode(id)
            }))
            .sort((a, b) => a.sort - b.sort);

        // Step 3: remove duplicates (keep first occurrence)
        const seen = new Set();
        const dedupedWarranty = orderedWarranty.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });

        // Step 3: map to colors
        const colors = dedupedWarranty.map((item, index) => {
            const cleanedId = String(item.id).replace(/\D/g, '');

            const patternColor = patternColors[item.originalIndex];
            const colorProduct = patternColor?.product || colorData[cleanedId];

            const label = item.textureCode
                ? (TEXTURE_TYPES[item.textureCode] || item.textureCode)
                : `${index + 1}`;

            return {
                index,
                originalIndex: item.originalIndex,
                originalId: item.id,
                currentId: patternColor?.currentId || cleanedId,
                label,
                textureCode: item.textureCode,
                sku: colorProduct?.sku || `#${cleanedId}`,
                product: colorProduct || null
            };
        });

        setDisplayColors(colors);
        dispatch(appActions.setDisplayColors(colors));
    }, [chosenPattern, colorData, patternColors, dispatch]);
    // Update displayColors when patternColors changes (e.g., color selection)
    useEffect(() => {
        if (displayColors.length === 0) {
            return;
        }

        const updatedDisplayColors = displayColors.map(displayColor => {
            const patternColor = patternColors.find(pc => pc.originalId === displayColor.originalId);
            if (patternColor) {
                return {
                    ...displayColor,
                    currentId: patternColor.currentId,
                    sku: patternColor.sku,
                    product: patternColor.product
                };
            }
            return displayColor;
        });

        // Only update if there are actual changes
        const hasChanges = updatedDisplayColors.some((color, i) => 
            color.currentId !== displayColors[i].currentId ||
            color.product !== displayColors[i].product
        );

        if (hasChanges) {
            setDisplayColors(updatedDisplayColors);
        }
    }, [patternColors]);

    const handleColorClick = (color) => {
        // Use originalIndex which tracks the actual position in patternColors
        dispatch(appActions.setSelectedColorIndex(color.originalIndex));
    };

    const isSelected = (color) => {
        if (selectedColorIndex === null) return false;
        return selectedColorIndex === color.originalIndex;
    };

    if (!chosenPattern) {
        return (
            <div className={styles.noPattern}>
                Please select a pattern first
            </div>
        );
    }

    return (
        <div className={styles.customizeColors}>
            {uniqueTextureTypes.length > 0 && (
                <div className={styles.textureLegend}>
                    {uniqueTextureTypes.map(type => (
                        <div key={type.code} className={styles.legendItem}>
                            {Array.isArray(TEXTURE_IMAGES[type.code]) ? (
                                TEXTURE_IMAGES[type.code].map((img, i) => (
                                    <img 
                                        key={i}
                                        src={img} 
                                        alt={type.label} 
                                        className={styles.legendIcon} 
                                    />
                                ))
                            ) : TEXTURE_IMAGES[type.code] ? (
                                <img 
                                    src={TEXTURE_IMAGES[type.code]} 
                                    alt={type.label} 
                                    className={styles.legendIcon} 
                                />
                            ) : (
                                <span className={`${styles.legendIcon} ${styles[`texture${type.code}`]}`}></span>
                            )}
                            <span className={styles.legendLabel}>{type.label}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.colorGrid}>
                {displayColors.map((color) => {
                    const selected = isSelected(color);
                    return (
                        <div
                            key={`${color.originalId}-${color.index}`}
                            className={`${styles.colorItem} ${selected ? styles.selected : ''}`}
                            onClick={() => handleColorClick(color)}
                        >
                            <div className={styles.colorImageWrapper}>
                                <div className={styles.colorImage}>
                                    {color.product ? (
                                        <ProductThumbnail
                                            product={color.product}
                                            size={80}
                                            alt={color.sku}
                                            className={styles.thumbnailImg}
                                        />
                                    ) : (
                                        <div className={styles.placeholder}></div>
                                    )}
                                </div>
                            </div>
                            <div className={styles.colorInfo}>
                                <span className={`${styles.colorLabel} ${styles[`texture${color.textureCode}`]}`}>{SLOT_LABELS[color.index] || color.index + 1}</span>
                                <span className={styles.colorSku}>{color.sku}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
